import type { Verification } from './vocabulary';
import { VERIFICATION_LABELS, type LegendaLang } from './vocabulary';

export interface CitationChipProps {
  /** The figure itself, already formatted for the locale. */
  value: string;
  /** Short source locator shown in muted ink, e.g. `с. 118`. */
  source: string;
  verification: Verification;
  lang: LegendaLang;
  /** Opens the evidence region. Omitted only in non-interactive previews. */
  onOpen?: () => void;
}

/**
 * A figure carried inside model prose.
 *
 * This is the enforcement mechanism behind constraint 6. A numeral may not
 * appear as plain text inside a `ModelProse` block — it must be a chip, which
 * means it arrives in mono, carrying its own verification rule on its left
 * edge and a link to its evidence region.
 *
 * The practical consequence: strip the model's sentences away and every number
 * still carries its provenance. Prose can never launder a value into looking
 * more settled than it is.
 */
export default function CitationChip({
  value,
  source,
  verification,
  lang,
  onOpen,
}: CitationChipProps) {
  const label = `${value} — ${VERIFICATION_LABELS[verification][lang]}, ${source}`;
  const className = `lg-cite lg-cite--${verification}`;

  if (!onOpen) {
    return (
      <span className={className} aria-label={label} data-verification={verification}>
        {value}
        <span className="lg-cite__source">{source}</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onOpen}
      aria-label={label}
      data-verification={verification}
    >
      {value}
      <span className="lg-cite__source">{source}</span>
    </button>
  );
}
